import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Upload, Loader, CheckCircle2, XCircle, FileText, AlertTriangle } from 'lucide-react';
import { enfantsApi, piecesJointesApi } from '../../utils/api';
import { formatMontant } from '../../utils/calculs';
import { openPdf } from '../../components/PdfPreview';

/**
 * Contrat d'accueil signé : import du PDF (DocuSign) lu par l'IA puis vérifié par la direction,
 * et résumé du contrat actif (signature, mandat SEPA, planning, coût mensuel).
 */
const JOURS = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi'];
const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
const fmtDate = (d) => (d ? d.split('-').reverse().join('/') : '—');
const fmtH = (h) => (h || h === 0 ? `${Number(h).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} h` : '—');

function Stat({ label, value, strong }) {
  return (
    <div>
      <div className="text-gray-500 text-xs">{label}</div>
      <div className={strong ? 'font-bold text-primary-600' : 'font-medium text-gray-800'}>{value || '—'}</div>
    </div>
  );
}

export default function ContratAccueilCard({ enfantId, onChange }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(null);   // conditions lues, en cours de vérification
  const [msg, setMsg] = useState(null);     // { type: 'ok'|'ko', text }
  const fileRef = useRef(null);

  const load = useCallback(() => {
    enfantsApi.getContratResume(enfantId).then(r => setData(r.data)).catch(() => setData(null));
  }, [enfantId]);
  useEffect(() => { load(); }, [load]);

  const deposer = async (file) => {
    if (!file) return;
    setBusy(true); setMsg(null); setForm(null);
    try {
      const fd = new FormData();
      fd.append('fichier', file);
      fd.append('entite_type', 'enfant');
      fd.append('entite_id', String(enfantId));
      fd.append('categorie', 'contrat');
      const up = await piecesJointesApi.upload(fd);
      const { data: r } = await enfantsApi.analyserContrat(enfantId, up.data.id);
      // Champs non lus : on reprend le contrat et le planning déjà enregistrés.
      const a = data?.contrat || {};
      const lu = r.extrait || {};
      const x = { ...lu };
      // (heures lissées et coût mensuel non repris : ils se recalculent si le tarif ou le volume change)
      ['date_signature', 'date_debut', 'date_fin', 'heures_hebdo', 'semaines_an', 'tarif_horaire', 'rum']
        .forEach(k => { if (x[k] == null || x[k] === '') x[k] = a[k] ?? null; });
      if (!(lu.jours || []).length) x.jours = data?.planning?.jours || [];
      const grid = {};
      JOURS.forEach(j => { grid[j] = { on: false, debut: '08:30', fin: '17:30' }; });
      (x.jours || []).forEach(j => { const k = String(j.jour || '').toLowerCase(); if (grid[k]) grid[k] = { on: true, debut: j.heure_debut || '08:30', fin: j.heure_fin || '17:30' }; });
      setForm({
        piece_id: up.data.id, piece: r.piece, ia: r.ia,
        date_signature: x.date_signature || '', date_debut: x.date_debut || '', date_fin: x.date_fin || '',
        heures_hebdo: x.heures_hebdo ?? '', semaines_an: x.semaines_an ?? 47, heures_mensuelles: x.heures_mensuelles ?? '',
        tarif_horaire: x.tarif_horaire ?? '', montant_mensuel: x.montant_mensuel ?? '', rum: x.rum || '',
        docusign_enveloppe: x.docusign_enveloppe || '',
        grid, appliquer_planning: (lu.jours || []).length > 0,
        alertes: [
          r.avertissement,
          r.ia && x.est_contrat === false && "ce document ne semble pas être un contrat d'accueil",
          r.ia && x.enfant_correspond === false && 'il ne semble pas concerner cet enfant',
          r.ia && x.signe === false && 'aucune signature de la famille détectée',
          !r.ia && !r.avertissement && "lecture automatique indisponible : complétez les champs à la main",
        ].filter(Boolean),
        resume: x.resume,
      });
    } catch (err) { setMsg({ type: 'ko', text: err.response?.data?.error || 'Dépôt ou lecture impossible.' }); }
    setBusy(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const enregistrer = async () => {
    setBusy(true); setMsg(null);
    try {
      const jours = JOURS.filter(j => form.grid[j].on).map(j => ({ jour: j, heure_debut: form.grid[j].debut, heure_fin: form.grid[j].fin }));
      const { grid, alertes, resume, piece, ia, ...champs } = form;
      const { data: r } = await enfantsApi.importerContrat(enfantId, { ...champs, jours });
      setData(r.resume); setForm(null);
      setMsg({ type: 'ok', text: 'Contrat signé enregistré.' });
      onChange && onChange();
    } catch (err) { setMsg({ type: 'ko', text: err.response?.data?.error || 'Enregistrement impossible.' }); }
    setBusy(false);
  };

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const setJour = (j, patch) => setForm(f => ({ ...f, grid: { ...f.grid, [j]: { ...f.grid[j], ...patch } } }));
  const c = data?.contrat;
  const jours = data?.planning?.jours || [];

  return (
    <div className="space-y-4">
      {/* Résumé du contrat actif */}
      {c ? (
        <div className="rounded-xl border border-gray-100 bg-gray-50/60 p-4 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="font-semibold text-gray-700 text-sm">Résumé du contrat</div>
            {data.piece && (
              <button type="button" onClick={() => openPdf(piecesJointesApi.viewUrl(data.piece.id), data.piece.nom_original)} className="text-xs text-navy-600 underline flex items-center gap-1">
                <FileText size={13} /> {data.piece.nom_original}
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
            <Stat label="Date de signature" value={fmtDate(c.date_signature)} />
            <Stat label="N° de mandat SEPA" value={data.rum ? `${data.rum}${data.rum_source === 'contrat' ? ' (lu au contrat)' : ''}` : 'Aucun mandat'} />
            <Stat label="Période" value={c.date_debut ? `${fmtDate(c.date_debut)}${c.date_fin ? ` → ${fmtDate(c.date_fin)}` : ''}` : '—'} />
            <Stat label="Taux horaire" value={c.tarif_horaire ? `${formatMontant(c.tarif_horaire)}/h` : '—'} />
            <Stat label="Heures / semaine" value={fmtH(c.heures_hebdo)} />
            <Stat label={`Heures mensuelles lissées (${c.semaines_an} sem. / 12)`} value={fmtH(c.heures_mensuelles)} />
            <Stat label="Coût mensuel" value={c.montant_mensuel ? formatMontant(c.montant_mensuel) : '—'} strong />
          </div>
          <div>
            <div className="text-gray-500 text-xs mb-1.5">Planning de l'enfant{data.planning?.source === 'devis' ? ' (repris du devis)' : ''}</div>
            {jours.length ? (
              <div className="flex flex-wrap gap-2">
                {jours.map(j => (
                  <span key={j.jour} className="text-xs rounded-lg bg-white border border-gray-200 px-2.5 py-1">
                    <span className="font-medium">{cap(j.jour)}</span> {j.heure_debut}–{j.heure_fin}
                  </span>
                ))}
              </div>
            ) : <div className="text-xs text-gray-400">Aucun planning renseigné.</div>}
          </div>
        </div>
      ) : data && (
        <p className="text-xs text-gray-400">Aucun contrat signé enregistré pour l'instant.</p>
      )}

      {/* Import du contrat signé */}
      {!form && (
        <div className="flex items-center gap-2 flex-wrap">
          <button type="button" disabled={busy} onClick={() => fileRef.current?.click()} className="btn-secondary text-xs">
            {busy ? <Loader size={14} className="animate-spin" /> : <Upload size={14} />} {busy ? 'Lecture du contrat…' : (c ? 'Remplacer par le contrat signé (DocuSign)' : 'Charger le contrat signé (DocuSign)')}
          </button>
          <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.docx" className="hidden" onChange={e => deposer(e.target.files?.[0])} />
          <span className="text-[11px] text-gray-400">Le PDF est rangé dans le dossier de l'enfant et ses conditions sont lues automatiquement.</span>
        </div>
      )}

      {form && (
        <div className="rounded-xl border border-navy-100 bg-white p-4 space-y-3">
          <div className="text-sm font-semibold text-gray-700">Vérifier les conditions lues dans « {form.piece} »</div>
          {form.resume && <div className="text-xs text-gray-500">Analyse : {form.resume}</div>}
          {form.alertes.length > 0 && (
            <div className="text-[12.5px] rounded px-3 py-2 bg-amber-50 text-amber-800 flex items-start gap-2">
              <AlertTriangle size={15} className="flex-shrink-0 mt-0.5" /> <span>À vérifier : {form.alertes.join(' ; ')}.</span>
            </div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
            {[
              ['date_signature', 'Date de signature', 'date'], ['date_debut', 'Début du contrat', 'date'], ['date_fin', 'Fin du contrat', 'date'],
              ['tarif_horaire', 'Taux horaire (€)', 'number'], ['heures_hebdo', 'Heures / semaine', 'number'], ['semaines_an', 'Semaines facturées / an', 'number'],
              ['heures_mensuelles', 'Heures mensuelles lissées', 'number'], ['montant_mensuel', 'Coût mensuel (€)', 'number'], ['rum', 'N° de mandat SEPA (RUM)', 'text'],
            ].map(([k, label, type]) => (
              <label key={k} className="block">
                <span className="text-gray-500">{label}</span>
                <input type={type} step="any" value={form[k]} onChange={set(k)} className="form-input mt-0.5 w-full" />
              </label>
            ))}
          </div>
          <p className="text-[11px] text-gray-400">Laissez « heures mensuelles » ou « coût mensuel » vides pour qu'ils soient calculés (heures/sem × semaines ÷ 12, puis × taux horaire).</p>
          <div>
            <div className="text-xs text-gray-500 mb-1">Planning du contrat</div>
            <div className="space-y-1">
              {JOURS.map(j => (
                <div key={j} className="flex items-center gap-2 text-xs">
                  <label className="flex items-center gap-1.5 w-24"><input type="checkbox" checked={form.grid[j].on} onChange={e => setJour(j, { on: e.target.checked })} /> {cap(j)}</label>
                  <input type="time" value={form.grid[j].debut} disabled={!form.grid[j].on} onChange={e => setJour(j, { debut: e.target.value })} className="form-input py-1 w-28" />
                  <span>→</span>
                  <input type="time" value={form.grid[j].fin} disabled={!form.grid[j].on} onChange={e => setJour(j, { fin: e.target.value })} className="form-input py-1 w-28" />
                </div>
              ))}
            </div>
            <label className="flex items-center gap-1.5 text-xs mt-2">
              <input type="checkbox" checked={form.appliquer_planning} onChange={e => setForm(f => ({ ...f, appliquer_planning: e.target.checked }))} />
              Utiliser ce planning comme planning contractuel de l'enfant
            </label>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" disabled={busy} onClick={enregistrer} className="btn-primary text-xs">
              {busy ? <Loader size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Enregistrer le contrat signé
            </button>
            <button type="button" disabled={busy} onClick={() => setForm(null)} className="btn-secondary text-xs">Annuler</button>
          </div>
        </div>
      )}

      {msg && (
        <div className={`text-[12.5px] rounded px-3 py-2 flex items-start gap-2 ${msg.type === 'ok' ? 'bg-emerald-50 text-emerald-700' : 'bg-white text-red-600 border border-red-100'}`}>
          {msg.type === 'ok' ? <CheckCircle2 size={15} className="flex-shrink-0 mt-0.5" /> : <XCircle size={15} className="flex-shrink-0 mt-0.5" />} {msg.text}
        </div>
      )}
    </div>
  );
}
